import { useState } from "react";
import {
  cancelEventRegistration,
  cancelTeamRegistration,
  friendlyRegistrationError,
} from "@/api/registrations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { logger, serializeError } from "@/lib/logger";

interface Props {
  eventId: number;
  isTeam: boolean;
  /** Called after a successful cancellation so the caller can refresh and collapse. */
  onCancelled: () => void;
  className?: string;
}

/** Destructive "Cancel registration" link, its confirmation, and the cancellation
 *  itself — used by the collapsed card and the expanded manage panel. */
export function CancelRegistrationLink({
  eventId,
  isTeam,
  onCancelled,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const { toast } = useToast();

  // The dialog stays open until the request settles: it's modal, so holding it
  // there keeps the panel's member controls inert rather than letting them race
  // a registration that's being deleted. On failure it stays open to retry.
  const handleConfirm = async () => {
    setPending(true);
    try {
      await (isTeam ? cancelTeamRegistration(eventId) : cancelEventRegistration(eventId));
      setOpen(false);
      toast({ title: isTeam ? "Team registration cancelled" : "Registration cancelled" });
      onCancelled();
    } catch (err) {
      logger.error("Couldn't cancel registration", serializeError(err));
      toast({
        title: "Couldn't cancel",
        description: friendlyRegistrationError(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={cn(
          "text-xs text-destructive/80 underline-offset-2 hover:text-destructive hover:underline",
          className,
        )}
        onClick={() => setOpen(true)}
      >
        {isTeam ? "Cancel team registration" : "Cancel registration"}
      </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onEscapeKeyDown={(e) => { if (pending) e.preventDefault(); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isTeam ? "Cancel team registration?" : "Cancel registration?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isTeam
                ? "This removes your whole team from this event. You can register again while the event is open."
                : "This removes you from this event. You can register again while the event is open."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Keep registration</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={pending}
              onClick={(e) => {
                e.preventDefault(); // Radix closes on click; we close on settle.
                void handleConfirm();
              }}
            >
              {isTeam ? "Cancel team" : "Cancel registration"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
